import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdKeyboardArrowDown,
  MdEvent,
  MdVpnKey,
  MdStore,
  MdTag,
} from "react-icons/md";

import styles from "./PromoSelector.module.scss";
import { DeliveryType } from "../../../common/types/enums";
import { CartItem, PromoInterface } from "../../../common/types/interfaces";

interface Props {
  applicablePromos: PromoInterface[];
  selectedPromo: PromoInterface | null;
  discountCode: string;
  cartItems: CartItem[];
  storeId?: number;
  deliveryType?: DeliveryType;
  onSelectPromo: (promo: PromoInterface | null) => void;
  onDiscountCodeChange: (code: string) => void;
}

export const PromoSelector: React.FC<Props> = ({
  applicablePromos,
  selectedPromo,
  discountCode,
  onSelectPromo,
  onDiscountCodeChange,
}) => {
  const [showModal, setShowModal] = useState(false);

  const handleSelect = (promo: PromoInterface | null) => {
    onSelectPromo(promo);
    setShowModal(false);
  };

  const formatDate = (date: Date | string) =>
    new Date(date).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const buttonText = selectedPromo
    ? `${selectedPromo.name} - ${selectedPromo.discount}%`
    : applicablePromos.length > 0
      ? `Доступно ${applicablePromos.length} промо-акций`
      : "Нет доступных промо-акций";

  const requiresCode = selectedPromo?.useDiscountCode;

  return (
    <div className={styles.Container}>
      <button
        type="button"
        className={styles.Button}
        onClick={() => setShowModal(true)}
        disabled={applicablePromos.length === 0 && !selectedPromo}
      >
        <span className={styles.ButtonText}>
          {selectedPromo ? (
            <>
              <span className={styles.Badge}>Промо</span>
              {selectedPromo.name}
            </>
          ) : (
            buttonText
          )}
        </span>
        <MdKeyboardArrowDown size={22} />
      </button>

      {requiresCode && (
        <div className={styles.CodeWrap}>
          <span className={styles.CodeLabel}>Введите промокод</span>
          <input
            className={styles.CodeInput}
            value={discountCode}
            onChange={(e) => onDiscountCodeChange(e.target.value)}
            placeholder="XXXX-XXXX-XXXX"
          />
          <span className={styles.CodeHint}>
            Для применения этой промо-акции необходим промокод
          </span>
        </div>
      )}

      {showModal &&
        createPortal(
          <div className={styles.Overlay} onClick={() => setShowModal(false)}>
            <div className={styles.Modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.ModalHeader}>
                <h3 className={styles.ModalTitle}>Выберите промо-акцию</h3>
                <button
                  type="button"
                  className={styles.Close}
                  onClick={() => setShowModal(false)}
                  aria-label="Закрыть"
                >
                  <MdClose size={22} />
                </button>
              </div>

              <div className={styles.List}>
                <button
                  type="button"
                  className={`${styles.Item} ${
                    !selectedPromo ? styles.Active : ""
                  }`}
                  onClick={() => handleSelect(null)}
                >
                  <span className={styles.ItemName}>Без промо-акции</span>
                </button>

                {applicablePromos.map((promo) => (
                  <button
                    key={promo.id}
                    type="button"
                    className={`${styles.Item} ${
                      selectedPromo?.id === promo.id ? styles.Active : ""
                    }`}
                    onClick={() => handleSelect(promo)}
                  >
                    <div className={styles.ItemHeader}>
                      <span className={styles.ItemName}>{promo.name}</span>
                      <span className={styles.ItemDiscount}>
                        {promo.discount}%
                      </span>
                    </div>

                    {promo.description && (
                      <span className={styles.ItemDescription}>
                        {promo.description}
                      </span>
                    )}

                    <div className={styles.ItemMeta}>
                      {promo.datesImportant && (
                        <span className={styles.Meta}>
                          <MdEvent size={12} />
                          {formatDate(promo.startDate)} —{" "}
                          {formatDate(promo.endDate)}
                        </span>
                      )}
                      {promo.useDiscountCode && (
                        <span className={styles.Meta}>
                          <MdVpnKey size={12} />
                          Нужен код
                        </span>
                      )}
                      {promo.useStores &&
                        (promo.storesFor?.length || 0) > 0 && (
                          <span className={styles.Meta}>
                            <MdStore size={12} />
                            {promo.storesFor?.length} магаз.
                          </span>
                        )}
                    </div>
                  </button>
                ))}

                {applicablePromos.length === 0 && (
                  <div className={styles.Empty}>
                    <MdTag size={40} />
                    <span>Нет доступных промо-акций для этого заказа</span>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
